import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { paymentsAPI, clientsAPI } from '../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarIcon,
  BanknotesIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Table, Card, PermissionGate, FileUpload, ConfirmModal } from '../components/ui';
import { PageHeader, StatusBadge, ActionIconButton, DataTableCard } from '../components/ui';
import PaymentSuccessModal, { PaymentSuccessData } from '../components/payments/PaymentSuccessModal';
import { Payment, Invoice, Client, TableColumn } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

const PAYMENT_METHODS = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA_BANCARIA', label: 'Trans. Bancaria' },
  { value: 'TRANSFERENCIA_MOVIL', label: 'Trans. Móvil' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'DEPOSITO_BANCARIO', label: 'Depósito' },
];

const METHOD_LABELS: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia Bancaria',
  TRANSFERENCIA_MOVIL: 'Transferencia Móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito Bancario',
};

const METHOD_BADGE_COLORS: Record<string, string> = {
  EFECTIVO: 'bg-emerald-100 text-emerald-800',
  TRANSFERENCIA_BANCARIA: 'bg-blue-100 text-blue-800',
  TRANSFERENCIA_MOVIL: 'bg-violet-100 text-violet-800',
  CHEQUE: 'bg-amber-100 text-amber-800',
  DEPOSITO_BANCARIO: 'bg-indigo-100 text-indigo-800',
};

const TRANSFER_METHODS = ['TRANSFERENCIA_BANCARIA', 'TRANSFERENCIA_MOVIL', 'DEPOSITO_BANCARIO'];

const getInvoiceBalance = (invoice: Invoice) =>
  invoice.balanceDueMinor != null ? invoice.balanceDueMinor / 100 : Number((invoice as any).amountDue || 0);

/* ─── Payment panel ─── */
const PaymentPanel: React.FC<{
  payment?: Payment | null;
  clients: Client[];
  onClose: () => void;
  onSave: (data: any) => void;
  isSaving: boolean;
}> = ({ payment, clients, onClose, onSave, isSaving }) => {
  const { hasPermission } = usePermissions();
  const canVerify = hasPermission('payments.verify');
  const isEdit = Boolean(payment);

  const [formData, setFormData] = useState({
    amount: payment?.amount || 0,
    paymentDate: payment?.paymentDate
      ? new Date(payment.paymentDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    paymentMethod: (payment?.paymentMethod || 'EFECTIVO') as string,
    reference: payment?.reference || '',
    notes: payment?.notes || '',
    clientId: payment?.clientId?.toString() || '',
    bankReference: (payment as any)?.bankReference || '',
    bankName: (payment as any)?.bankName || '',
    accountNumber: (payment as any)?.accountNumber || '',
    transferDate: (payment as any)?.transferDate
      ? new Date((payment as any).transferDate).toISOString().split('T')[0]
      : '',
    receiptFile: (payment as any)?.receiptFile || '',
    isVerified: payment?.isVerified || false,
  });

  // allocations: invoiceId (string) → amount (decimal string)
  const [allocations, setAllocations] = useState<Record<string, string>>({});

  const [clientSearch, setClientSearch] = useState(
    payment?.client ? `${payment.client.name} ${(payment.client as any).lastname || ''}`.trim() : ''
  );
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current && !clientInputRef.current.contains(e.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: clientInvoices, isLoading: invoicesLoading } = useQuery<{ data: { data: Invoice[] } }>(
    ['pending-invoices', formData.clientId],
    () => paymentsAPI.getPendingInvoices(parseInt(formData.clientId)),
    { enabled: Boolean(formData.clientId) && !isEdit, staleTime: 30_000 }
  );

  const filteredClients = useMemo(
    () =>
      clients
        .filter(c =>
          `${c.name} ${(c as any).lastname || ''} ${c.cedula}`
            .toLowerCase()
            .includes(clientSearch.toLowerCase())
        )
        .slice(0, 8),
    [clients, clientSearch]
  );

  const selectedClient = clients.find(c => String(c.id) === formData.clientId);
  const invoiceList: Invoice[] = clientInvoices?.data?.data || [];
  const isTransferMethod = TRANSFER_METHODS.includes(formData.paymentMethod);

  // Allocation calculations
  const allocatedTotal = Object.values(allocations).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const creditBalance = Math.max(formData.amount - allocatedTotal, 0);
  const overdraft = allocatedTotal > formData.amount + 0.001;

  const handleSelectClient = (client: Client) => {
    setClientSearch(`${client.name} ${(client as any).lastname || ''}`.trim());
    setFormData(prev => ({ ...prev, clientId: String(client.id), amount: 0 }));
    setAllocations({});
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setClientSearch('');
    setFormData(prev => ({ ...prev, clientId: '', amount: 0 }));
    setAllocations({});
  };

  const toggleAllocation = (invoice: Invoice) => {
    const key = String(invoice.id);
    setAllocations(prev => {
      if (prev[key] !== undefined) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: String(getInvoiceBalance(invoice).toFixed(2)) };
    });
  };

  const updateAllocationAmount = (invoiceId: string, value: string) => {
    setAllocations(prev => ({ ...prev, [invoiceId]: value }));
  };

  const fillAllPending = () => {
    let remaining = formData.amount;
    const next: Record<string, string> = {};
    for (const inv of invoiceList) {
      if (remaining <= 0) break;
      const bal = getInvoiceBalance(inv);
      const apply = Math.min(bal, remaining);
      next[String(inv.id)] = apply.toFixed(2);
      remaining -= apply;
    }
    setAllocations(next);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'amount'
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const buildPayload = () => {
    const base: Record<string, unknown> = {
      clientId: parseInt(formData.clientId, 10),
      amount: formData.amount,
      paymentDate: formData.paymentDate,
      paymentMethod: formData.paymentMethod,
      reference: formData.reference || undefined,
      notes: formData.notes || undefined,
      isVerified: formData.isVerified,
    };

    if (isTransferMethod) {
      base.bankReference = formData.bankReference || undefined;
      base.bankName = formData.bankName || undefined;
      base.accountNumber = formData.accountNumber || undefined;
      base.transferDate = formData.transferDate || undefined;
      base.receiptFile = formData.receiptFile || undefined;
    }

    if (!isEdit) {
      const allocationArray = Object.entries(allocations)
        .filter(([, v]) => parseFloat(v) > 0)
        .map(([invoiceId, v]) => ({
          invoiceId: parseInt(invoiceId, 10),
          amountMinor: Math.round(parseFloat(v) * 100),
        }));
      if (allocationArray.length > 0) {
        base.allocations = allocationArray;
      }
    }

    return base;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) { toast.error('Seleccione un cliente'); return; }
    if (!formData.amount || formData.amount <= 0) { toast.error('Ingrese un monto válido'); return; }
    if (overdraft) { toast.error('El monto aplicado a facturas supera el total del pago'); return; }
    onSave(buildPayload());
  };

  const inputCls =
    'w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white overflow-hidden lg:inset-auto lg:z-auto lg:w-[420px] lg:shrink-0 lg:rounded-2xl lg:border lg:border-slate-200 lg:shadow-xl lg:sticky lg:top-6 lg:max-h-[calc(100vh-140px)]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50 shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            {isEdit ? 'Editar pago' : 'Registrar pago'}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isEdit ? 'Modifica los datos del registro' : 'Complete los campos para registrar'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar panel"
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} id="payment-form">
          <div className="p-5 space-y-6">

            {/* Client */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cliente *</p>

              {selectedClient ? (
                <div className="flex items-center gap-3 px-3 py-2.5 bg-indigo-50 rounded-xl border border-indigo-200">
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <UserIcon className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-indigo-900 truncate">
                      {selectedClient.name} {(selectedClient as any).lastname || ''}
                    </p>
                    <p className="text-xs text-indigo-600">{selectedClient.cedula}</p>
                  </div>
                  {!isEdit && (
                    <button type="button" onClick={handleClearClient} aria-label="Quitar cliente" className="text-indigo-400 hover:text-indigo-600 transition-colors">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      ref={clientInputRef}
                      type="text"
                      placeholder="Buscar por nombre o cédula…"
                      value={clientSearch}
                      onChange={e => {
                        setClientSearch(e.target.value);
                        setShowClientDropdown(true);
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                    />
                  </div>

                  {showClientDropdown && filteredClients.length > 0 && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-lg overflow-y-auto max-h-[200px]"
                    >
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors border-b border-slate-50 last:border-0"
                        >
                          <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <UserIcon className="h-3.5 w-3.5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {client.name} {(client as any).lastname || ''}
                            </p>
                            <p className="text-xs text-slate-500">{client.cedula}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {showClientDropdown && clientSearch.length > 1 && filteredClients.length === 0 && (
                    <div className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-lg px-4 py-3">
                      <p className="text-sm text-slate-500">No se encontraron clientes</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Amount & Date */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Monto y fecha</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Monto *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400 pointer-events-none">$</span>
                    <input
                      type="number"
                      step="0.01"
                      name="amount"
                      value={formData.amount || ''}
                      onChange={handleChange}
                      placeholder="0.00"
                      required
                      className="w-full pl-7 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Fecha *</label>
                  <input
                    type="date"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    aria-label="Fecha del pago"
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            {/* Payment method */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Método de pago</p>
              <div className="flex flex-wrap gap-2">
                {PAYMENT_METHODS.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, paymentMethod: m.value }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      formData.paymentMethod === m.value
                        ? 'border-primary-400 bg-primary-50 text-primary-700 ring-1 ring-primary-200'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Referencia</label>
                <input
                  type="text"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  placeholder="Número de referencia del pago"
                  className={inputCls}
                />
              </div>
            </div>

            {/* Transfer details */}
            {isTransferMethod && (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Datos bancarios</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Ref. bancaria</label>
                    <input type="text" name="bankReference" value={formData.bankReference} onChange={handleChange} placeholder="Nro. referencia" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Banco</label>
                    <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} placeholder="Nombre del banco" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">N° cuenta</label>
                    <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="Número de cuenta" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Fecha transf.</label>
                    <input type="date" name="transferDate" value={formData.transferDate} onChange={handleChange} aria-label="Fecha de la transferencia" className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Comprobante</label>
                  <FileUpload
                    onFileSelect={file => {
                      if (!file) setFormData(prev => ({ ...prev, receiptFile: '' }));
                    }}
                    onFileUpload={async file => {
                      const r = await paymentsAPI.uploadReceipt(file);
                      const filePath: string = r.data.data.filePath;
                      setFormData(prev => ({ ...prev, receiptFile: filePath }));
                      return filePath;
                    }}
                    accept="image/*,.pdf"
                    maxSize={5}
                  />
                  {formData.receiptFile && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium truncate">✓ {formData.receiptFile}</p>
                  )}
                </div>

                {canVerify && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isVerified"
                      checked={formData.isVerified}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-primary-400"
                    />
                    <span className="text-sm text-slate-700">Marcar como verificado</span>
                  </label>
                )}
              </div>
            )}

            {/* Multi-invoice allocations (create only) */}
            {!isEdit && formData.clientId && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Aplicar a facturas
                  </p>
                  {invoiceList.length > 0 && formData.amount > 0 && (
                    <button
                      type="button"
                      onClick={fillAllPending}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      Aplicar a todas
                    </button>
                  )}
                </div>

                {invoicesLoading ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-slate-500">
                    <LoadingSpinner size="sm" />
                    <span>Cargando facturas…</span>
                  </div>
                ) : invoiceList.length === 0 ? (
                  <p className="text-xs text-slate-500 py-1">Sin facturas pendientes — el pago quedará como crédito.</p>
                ) : (
                  <div className="space-y-1.5">
                    {invoiceList.map(invoice => {
                      const balance = getInvoiceBalance(invoice);
                      const key = String(invoice.id);
                      const isSelected = allocations[key] !== undefined;
                      const allocationAmt = allocations[key] ?? '';
                      return (
                        <div
                          key={invoice.id}
                          className={`rounded-xl border transition-all ${
                            isSelected
                              ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
                              : 'border-slate-200 bg-white'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleAllocation(invoice)}
                            className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                          >
                            <div className="flex items-center gap-2.5">
                              <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                isSelected ? 'border-primary-500 bg-primary-500' : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircleIcon className="h-3 w-3 text-white" />}
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${isSelected ? 'text-primary-800' : 'text-slate-800'}`}>
                                  {(invoice as any).invoiceNumber || `Factura #${invoice.id}`}
                                </p>
                                <p className={`text-xs mt-0.5 ${isSelected ? 'text-primary-600' : 'text-slate-500'}`}>
                                  Saldo: ${balance.toLocaleString('es-DO', { minimumFractionDigits: 2 })} · Vence: {new Date((invoice as any).dueDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </button>

                          {isSelected && (
                            <div className="px-3 pb-2.5">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 shrink-0">Monto a aplicar:</span>
                                <div className="relative flex-1">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={balance}
                                    value={allocationAmt}
                                    onChange={e => updateAllocationAmount(key, e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    className="w-full pl-6 pr-2 py-1.5 text-sm rounded-lg border border-primary-300 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                                    aria-label={`Monto a aplicar a factura ${invoice.id}`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateAllocationAmount(key, String(balance.toFixed(2)))}
                                  className="text-xs text-slate-400 hover:text-indigo-600 transition-colors shrink-0"
                                  title="Completar saldo"
                                >
                                  Max
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Allocation summary */}
                {Object.keys(allocations).length > 0 && (
                  <div className={`rounded-xl p-3 border text-xs space-y-1 ${
                    overdraft ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'
                  }`}>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Total pago</span>
                      <span className="font-semibold text-slate-800">
                        ${formData.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Aplicado a facturas</span>
                      <span className={`font-semibold ${overdraft ? 'text-rose-700' : 'text-slate-800'}`}>
                        ${allocatedTotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {overdraft ? (
                      <div className="flex items-center gap-1 text-rose-600 font-medium pt-1">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        Monto aplicado supera el total del pago
                      </div>
                    ) : creditBalance > 0.005 ? (
                      <div className="flex justify-between pt-1 border-t border-slate-200">
                        <span className="text-emerald-600 font-medium">Crédito a favor</span>
                        <span className="font-bold text-emerald-700">
                          ${creditBalance.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between pt-1 border-t border-slate-200">
                        <span className="text-emerald-600 font-medium">Saldo cubierto</span>
                        <span className="text-emerald-600">✓</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Notas</p>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Observaciones adicionales (opcional)"
                className={`${inputCls} resize-none`}
              />
            </div>

          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center gap-3">
        {formData.amount > 0 && (
          <div>
            <p className="text-xs text-slate-500 leading-none">Total</p>
            <p className="text-lg font-bold text-slate-900 leading-tight">
              ${formData.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </p>
            {!isEdit && Object.keys(allocations).length > 0 && creditBalance > 0.005 && (
              <p className="text-xs text-emerald-600 leading-none">
                +${creditBalance.toLocaleString('es-DO', { minimumFractionDigits: 2 })} crédito
              </p>
            )}
          </div>
        )}
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="payment-form"
            disabled={isSaving || !formData.clientId || !formData.amount || overdraft}
            className="px-5 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-sm hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Guardando…' : isEdit ? 'Actualizar' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Payments page ─── */
const Payments: React.FC = () => {
  const navigate = useNavigate();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [filterMethod, setFilterMethod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'warning', onConfirm: () => {} });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successPayment, setSuccessPayment] = useState<PaymentSuccessData | null>(null);
  const [successCreditMinor, setSuccessCreditMinor] = useState(0);

  const queryClient = useQueryClient();
  const resetPage = () => setCurrentPage(1);

  const { data: clientsData } = useQuery<{ data: { data: Client[] } }>(
    'clients',
    clientsAPI.getAll,
    { staleTime: 60_000 }
  );
  const clients: Client[] = clientsData?.data?.data || [];

  const { data: payments, isLoading } = useQuery<{
    data: { data: Payment[]; total: number; totalPages: number; page: number };
  }>(
    ['payments', filterMethod, filterStatus, filterClient, filterStartDate, filterEndDate, currentPage],
    () =>
      paymentsAPI.getAll({
        paymentMethod: filterMethod || undefined,
        status: filterStatus || undefined,
        clientId: filterClient || undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      })
  );

  const { data: paymentStats } = useQuery<{ data: any }>('payment-stats', () => paymentsAPI.getStats());

  const handlePanelClose = () => { setIsPanelOpen(false); setSelectedPayment(null); };

  const createMutation = useMutation(paymentsAPI.create, {
    onSuccess: (response: any) => {
      queryClient.invalidateQueries('payments');
      queryClient.invalidateQueries('payment-stats');
      queryClient.invalidateQueries('invoices');
      toast.success('Pago registrado exitosamente');
      const warnings: string[] = response?.data?.warnings || [];
      warnings.forEach(w => toast(w, { icon: '⚠️', duration: 6000 }));
      const result = response?.data?.data;
      const payment = result?.payment || null;
      if (payment) {
        setSuccessPayment(payment);
        setSuccessCreditMinor(result?.creditBalanceMinor || 0);
        setSuccessModalOpen(true);
      }
      handlePanelClose();
    },
    onError: (error: any) => {
      toast.error('Error al registrar el pago: ' + (error.response?.data?.error || error.message));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => paymentsAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payments');
        queryClient.invalidateQueries('payment-stats');
        toast.success('Pago actualizado exitosamente');
        handlePanelClose();
      },
      onError: (error: any) => {
        toast.error('Error al actualizar el pago: ' + (error.response?.data?.error || error.message));
      },
    }
  );

  const deleteMutation = useMutation((id: number) => paymentsAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('payments');
      queryClient.invalidateQueries('payment-stats');
      toast.success('Pago anulado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al anular el pago: ' + (error.response?.data?.error || error.message));
    },
  });

  const verifyMutation = useMutation((id: number) => paymentsAPI.verify(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('payments');
      queryClient.invalidateQueries('payment-stats');
      toast.success('Pago verificado exitosamente');
    },
    onError: (error: any) => {
      toast.error('Error al verificar el pago: ' + (error.response?.data?.error || error.message));
    },
  });

  const handleCreate = () => { setSelectedPayment(null); setIsPanelOpen(true); };
  const handleEdit = (payment: Payment) => { setSelectedPayment(payment); setIsPanelOpen(true); };

  const handleSave = (data: any) => {
    if (selectedPayment) {
      updateMutation.mutate({ id: selectedPayment.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDownloadReceipt = async (id: number) => {
    try {
      const response = await paymentsAPI.downloadReceipt(id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo_pago_${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el recibo PDF');
    }
  };

  const handleDelete = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Anular Pago',
      message: '¿Está seguro de que desea anular este pago? Las facturas asociadas serán revertidas.',
      type: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleVerify = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Verificar Pago',
      message: '¿Confirma la verificación de este pago? Una vez verificado no podrá modificarse.',
      type: 'warning',
      onConfirm: () => {
        verifyMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const getPaymentStatusBadge = (payment: Payment) => {
    const status = payment.status || (payment.isVerified ? 'CONFIRMED' : 'PENDING');
    const map: Record<string, { label: string; tone: 'success' | 'danger' | 'warning' | 'neutral' | 'info' }> = {
      PENDING: { label: 'Pendiente', tone: 'info' },
      CONFIRMED: { label: 'Confirmado', tone: 'success' },
      CANCELLED: { label: 'Anulado', tone: 'danger' },
      REFUNDED: { label: 'Reembolsado', tone: 'neutral' },
      PARTIAL_REFUND: { label: 'Reemb. parcial', tone: 'warning' },
    };
    const m = map[status] || map.PENDING;
    return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>;
  };

  const columns: TableColumn<Payment>[] = [
    {
      key: 'client',
      label: 'Cliente',
      render: (_, payment) => (
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
            <CreditCardIcon className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              {payment.client?.name || (payment.clientId ? `Cliente #${payment.clientId}` : 'N/A')}
            </p>
            <p className="text-xs text-slate-500">{payment.client?.cedula || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      sortable: true,
      render: (_, payment) => (
        <span className="text-sm font-bold text-slate-900">
          ${(payment.amountMinor != null ? payment.amountMinor / 100 : Number(payment.amount || 0)).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'paymentMethod',
      label: 'Método',
      render: (_, payment) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${METHOD_BADGE_COLORS[payment.paymentMethod] || 'bg-slate-100 text-slate-700'}`}>
          {METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod}
        </span>
      ),
    },
    {
      key: 'paymentDate',
      label: 'Fecha',
      sortable: true,
      render: value => (
        <div className="flex items-center gap-1 text-sm text-slate-700">
          <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
          {new Date(value).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'allocations',
      label: 'Facturas',
      render: (_, payment) => {
        const count = payment.allocations?.length || 0;
        return (
          <span className={`text-sm font-medium ${count > 0 ? 'text-indigo-700' : 'text-slate-400'}`}>
            {count > 0 ? count : '—'}
          </span>
        );
      },
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: value => <span className="text-sm text-slate-600 font-mono">{value || '—'}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, payment) => getPaymentStatusBadge(payment),
    },
    {
      key: 'id',
      label: 'Acciones',
      className: 'text-right',
      render: (_, payment) => (
        <div className="flex justify-end items-center gap-1">
          <ActionIconButton title="Ver detalle" onClick={() => navigate(`/payments/${payment.id}`)} tone="info">
            <EyeIcon className="h-4 w-4" />
          </ActionIconButton>
          <ActionIconButton title="Descargar recibo PDF" onClick={() => handleDownloadReceipt(payment.id)} tone="neutral">
            <ArrowDownTrayIcon className="h-4 w-4" />
          </ActionIconButton>
          <PermissionGate permission="payments.update">
            {payment.status === 'PENDING' && (
              <ActionIconButton title="Editar" onClick={() => handleEdit(payment)}>
                <PencilIcon className="h-4 w-4" />
              </ActionIconButton>
            )}
          </PermissionGate>
          <PermissionGate permission="payments.verify">
            {(payment.status === 'PENDING' || (!payment.status && !payment.isVerified)) &&
              payment.paymentMethod !== 'EFECTIVO' &&
              payment.paymentMethod !== 'CHEQUE' && (
                <ActionIconButton title="Verificar" onClick={() => handleVerify(payment.id)} tone="success">
                  <CheckCircleIcon className="h-4 w-4" />
                </ActionIconButton>
              )}
          </PermissionGate>
          <PermissionGate permission="payments.delete">
            {payment.status !== 'CANCELLED' && payment.status !== 'REFUNDED' && (
              <ActionIconButton title="Anular" onClick={() => handleDelete(payment.id)} tone="danger">
                <TrashIcon className="h-4 w-4" />
              </ActionIconButton>
            )}
          </PermissionGate>
        </div>
      ),
    },
  ];

  const statusRows = paymentStats?.data?.paymentsByStatus || [];
  const countByStatus = (status: string) =>
    statusRows.find((row: any) => row.status === status)?._count?.id || 0;
  const hasFilters = !!(filterMethod || filterStatus || filterClient || filterStartDate || filterEndDate);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Gestión de Pagos"
        subtitle="Registro, conciliación y verificación de pagos de clientes."
        actions={
          <PermissionGate permission="payments.create">
            <Button
              onClick={handleCreate}
              className={`transition-all ${isPanelOpen && !selectedPayment ? 'bg-indigo-700 ring-2 ring-indigo-300' : 'bg-slate-800 hover:bg-slate-900'} text-white`}
              size="md"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nuevo Pago
            </Button>
          </PermissionGate>
        }
      />

      {/* Stats */}
      {paymentStats?.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl">
                <BanknotesIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Total pagos</p>
                <p className="text-2xl font-bold text-slate-900">{paymentStats.data.totalPayments || 0}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Confirmados</p>
                <p className="text-2xl font-bold text-slate-900">{countByStatus('CONFIRMED')}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 rounded-xl">
                <ClockIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Pendientes</p>
                <p className="text-2xl font-bold text-slate-900">{countByStatus('PENDING')}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-violet-50 rounded-xl">
                <DocumentTextIcon className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Ingresos confirmados</p>
                <p className="text-xl font-bold text-slate-900">
                  ${Number(paymentStats.data.totalRevenue || 0).toLocaleString('es-DO')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Mobile backdrop when panel is open */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={handlePanelClose}
          aria-hidden="true"
        />
      )}

      {/* Content: list + panel */}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">

        {/* List area */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Compact filter bar */}
          <Card padding="sm" className="border border-slate-100">
            <div className="overflow-x-auto">
            <div className="flex items-center gap-3 min-w-max">
              <div className="flex items-center gap-1.5 text-slate-400">
                <FunnelIcon className="h-4 w-4" />
                <span className="text-xs font-semibold text-slate-500">Filtros</span>
              </div>

              <select
                value={filterClient}
                onChange={e => { setFilterClient(e.target.value); resetPage(); }}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                aria-label="Filtrar por cliente"
              >
                <option value="">Todos los clientes</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {(c as any).lastname || ''}
                  </option>
                ))}
              </select>

              <select
                value={filterMethod}
                onChange={e => { setFilterMethod(e.target.value); resetPage(); }}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                aria-label="Filtrar por método"
              >
                <option value="">Todos los métodos</option>
                {PAYMENT_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={e => { setFilterStatus(e.target.value); resetPage(); }}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                aria-label="Filtrar por estado"
              >
                <option value="">Todos los estados</option>
                <option value="PENDING">Pendiente</option>
                <option value="CONFIRMED">Confirmado</option>
                <option value="CANCELLED">Anulado</option>
                <option value="REFUNDED">Reembolsado</option>
                <option value="PARTIAL_REFUND">Reemb. parcial</option>
              </select>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Desde</span>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={e => { setFilterStartDate(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  aria-label="Fecha desde"
                />
                <span className="text-xs text-slate-500">Hasta</span>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={e => { setFilterEndDate(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  aria-label="Fecha hasta"
                />
              </div>

              {hasFilters && (
                <button
                  type="button"
                  onClick={() => { setFilterMethod(''); setFilterStatus(''); setFilterClient(''); setFilterStartDate(''); setFilterEndDate(''); resetPage(); }}
                  className="ml-auto text-xs font-medium text-rose-500 hover:text-rose-700 transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
            </div>

            {/* Active filter chips */}
            {hasFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                {filterClient && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                    {clients.find(c => String(c.id) === filterClient)?.name || `Cliente #${filterClient}`}
                    <button type="button" onClick={() => { setFilterClient(''); resetPage(); }} aria-label="Quitar filtro de cliente" className="text-indigo-400 hover:text-indigo-700">
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filterMethod && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                    {METHOD_LABELS[filterMethod]}
                    <button type="button" onClick={() => { setFilterMethod(''); resetPage(); }} aria-label="Quitar filtro de método" className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filterStatus && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                    {filterStatus === 'PENDING' ? 'Pendiente' : filterStatus === 'CONFIRMED' ? 'Confirmado' : filterStatus === 'CANCELLED' ? 'Anulado' : filterStatus === 'REFUNDED' ? 'Reembolsado' : 'Reemb. parcial'}
                    <button type="button" onClick={() => { setFilterStatus(''); resetPage(); }} aria-label="Quitar filtro de estado" className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filterStartDate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                    Desde {new Date(filterStartDate).toLocaleDateString()}
                    <button type="button" onClick={() => { setFilterStartDate(''); resetPage(); }} aria-label="Quitar filtro de fecha inicio" className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filterEndDate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                    Hasta {new Date(filterEndDate).toLocaleDateString()}
                    <button type="button" onClick={() => { setFilterEndDate(''); resetPage(); }} aria-label="Quitar filtro de fecha fin" className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </Card>

          {/* Table */}
          <DataTableCard>
            <Table
              data={payments?.data?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No se encontraron pagos"
              rowClassName="hover:bg-slate-50"
            />
          </DataTableCard>

          {/* Pagination */}
          {payments?.data?.totalPages != null && payments.data.totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-xs text-slate-500">
                Página {payments.data.page} de {payments.data.totalPages} — {payments.data.total} pagos en total
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Anterior
                </button>
                <span className="text-xs text-slate-600 px-2">
                  {currentPage} / {payments.data.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(payments.data.totalPages, p + 1))}
                  disabled={currentPage >= payments.data.totalPages}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Side panel */}
        {isPanelOpen && (
          <PaymentPanel
            payment={selectedPayment}
            clients={clients}
            onClose={handlePanelClose}
            onSave={handleSave}
            isSaving={createMutation.isLoading || updateMutation.isLoading}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={deleteMutation.isLoading || verifyMutation.isLoading}
      />

      <PaymentSuccessModal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          setSuccessPayment(null);
          setSuccessCreditMinor(0);
        }}
        payment={successPayment}
        creditBalanceMinor={successCreditMinor}
      />
    </div>
  );
};

export default Payments;
